/**
 * Turning a connected wallet into a session.
 *
 * Reown gets us an address. This gets us proof the visitor controls it: a nonce
 * from our server, a SIWE message signed by the wallet, and a cookie in return.
 *
 * The one subtlety worth stating: signing is *not* automatic on connect. A
 * wallet popping a signature request the instant someone clicks "connect" is
 * how phishing flows behave, and it trains people to sign without reading.
 * Connecting is free; signing happens when the visitor asks to sign in, or at
 * the moment they try to do something that needs an account.
 */
import { useAppKit, useDisconnect } from '@reown/appkit/react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useState } from 'react'
import { createSiweMessage } from 'viem/siwe'
import { useAccount, useChainId, useSignMessage } from 'wagmi'

import { api, ApiError, type User } from '../lib/api'
import { walletReady } from '../lib/appkit'

export const meKey = ['auth', 'me'] as const

export function useUser() {
  return useQuery({
    queryKey: meKey,
    queryFn: async () => {
      try {
        const { user } = await api.get<{ user: User }>('/auth/me')
        return user
      } catch (err) {
        // 401 is the normal signed-out state, not a failure worth retrying.
        if (err instanceof ApiError && err.status === 401) return null
        throw err
      }
    },
    staleTime: 30_000,
    retry: (count, err) => !(err instanceof ApiError && err.status === 401) && count < 2,
  })
}

export function useAuth() {
  const queryClient = useQueryClient()
  const { address, isConnected } = useAccount()
  const chainId = useChainId()
  const { signMessageAsync } = useSignMessage()
  const { open } = useAppKit()
  const { disconnect } = useDisconnect()

  const { data: user, isLoading } = useUser()
  const [error, setError] = useState<string | null>(null)

  const signIn = useMutation({
    mutationFn: async () => {
      if (!address) throw new Error('Connect a wallet first')

      const { nonce } = await api.post<{ nonce: string }>('/auth/nonce')

      const message = createSiweMessage({
        address,
        chainId,
        domain: window.location.host,
        nonce,
        uri: window.location.origin,
        version: '1',
        statement:
          'Sign in to Elyxr. This signature proves you own this wallet. It authorises no transaction and moves no funds.',
      })

      const signature = await signMessageAsync({ message })
      const result = await api.post<{ user: User }>('/auth/verify', { message, signature })
      return result.user
    },
    onSuccess: (signedIn) => {
      setError(null)
      queryClient.setQueryData(meKey, signedIn)
      queryClient.invalidateQueries()
    },
    onError: (err: unknown) => {
      // A rejected signature is a decision, not a fault; say nothing about it.
      const message = err instanceof Error ? err.message : 'Sign-in failed'
      setError(/rejected|denied|User rejected/i.test(message) ? null : message)
    },
  })

  const signOut = useMutation({
    mutationFn: async () => {
      await api.post('/auth/logout')
      await disconnect()
    },
    onSuccess: () => {
      queryClient.setQueryData(meKey, null)
      queryClient.invalidateQueries()
    },
  })

  /**
   * The single entry point the UI calls. Walks whichever part of the flow is
   * still outstanding, so one button can mean "connect", "sign in", or nothing
   * depending on where the visitor already is.
   */
  const connectAndSignIn = useCallback(async () => {
    if (!walletReady) {
      setError('Wallet sign-in is not configured: set VITE_REOWN_PROJECT_ID.')
      return
    }
    if (!isConnected) {
      await open()
      return
    }
    if (!user) await signIn.mutateAsync().catch(() => undefined)
  }, [isConnected, open, signIn, user])

  return {
    user: user ?? null,
    isLoading,
    isConnected,
    address,
    /** Wallet attached but no session yet — the state the header calls out. */
    needsSignature: isConnected && !user && !isLoading,
    isSigningIn: signIn.isPending,
    error,
    clearError: () => setError(null),
    connectAndSignIn,
    signIn: () => signIn.mutateAsync().catch(() => undefined),
    signOut: () => signOut.mutateAsync(),
    openAccount: () => open({ view: 'Account' }),
  }
}
